import { useEffect, useRef, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase } from "../lib/supabase";

export type RealtimeStatus = "connecting" | "connected" | "disconnected";

// Merchant-relevante Live-Events (reine Signale).
const MERCHANT_EVENTS = new Set(["NEW_ORDER", "MERCHANT_REMINDER"]);

interface Params {
  profileId: string | null;
  // Voller Server-Refresh; optional die order_id eines MERCHANT_REMINDER.
  onRefresh: (reminderOrderId?: string) => void;
}

// Abonniert public.notifications INSERT, gefiltert auf eigene profile_id
// (RLS bleibt die echte Grenze). Realtime ist nur Signal => onRefresh.
// Enthält Reconnect-/Catch-up- und Visibility-/Online-Refresh, debounced.
export function useMerchantRealtime({ profileId, onRefresh }: Params): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>("connecting");
  const debounce = useRef<number | null>(null);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!profileId) return;
    const sb = getSupabase();
    let channel: RealtimeChannel | null = null;

    const scheduleRefresh = (reminderOrderId?: string) => {
      if (debounce.current !== null) clearTimeout(debounce.current);
      debounce.current = window.setTimeout(() => onRefreshRef.current(reminderOrderId), 150);
    };

    channel = sb
      .channel(`merchant-notifications:${profileId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `profile_id=eq.${profileId}` },
        (payload) => {
          const row = payload.new as { type?: string; data?: { order_id?: string } };
          if (row.type && MERCHANT_EVENTS.has(row.type)) {
            scheduleRefresh(row.type === "MERCHANT_REMINDER" ? row.data?.order_id : undefined);
          }
        },
      )
      .subscribe((s) => {
        if (s === "SUBSCRIBED") { setStatus("connected"); scheduleRefresh(); } // Catch-up
        else if (s === "CHANNEL_ERROR" || s === "TIMED_OUT" || s === "CLOSED") setStatus("disconnected");
        else setStatus("connecting");
      });

    // Netz weg => Badge sofort auf "Verbindung unterbrochen"; der Channel
    // kann das Aus noch nicht gemeldet haben. [PWA-VAL-003]
    const onOffline = () => setStatus("disconnected");
    // Netz zurück => erst "connecting"; "Live verbunden" erst wieder, wenn der
    // Channel real SUBSCRIBED meldet (siehe subscribe-Callback). Catch-up-
    // Refresh bleibt erhalten. [PWA-VAL-003]
    const onOnline = () => { setStatus("connecting"); scheduleRefresh(); };
    // Rückkehr aus Hintergrund => voller Refresh (Status unverändert lassen).
    const onVisible = () => { if (document.visibilityState === "visible") scheduleRefresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (debounce.current !== null) clearTimeout(debounce.current);
      if (channel) void sb.removeChannel(channel);
    };
  }, [profileId]);

  return status;
}
